import React from 'react';
import Typography from '@material-ui/core/Typography';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import CloudBrowser from 'components/CloudBrowser/CloudBrowser';
import { authenticateProvider, changeFolder, removeFolder, updateFolder } from 'redux/modules/cloudFolders';
import Grid from '@material-ui/core/Grid';
import classNames from 'classnames';
import { FolderIcon, DeleteIcon } from 'theme/icons';
import { fetchSession } from 'redux/modules/session';
import { showModal } from 'redux/modules/modal';
import { Table, TableBody, TableRow, TableCell } from '@material-ui/core';
import { DropboxIcon, GoogleDriveIcon, OneDriveIcon } from 'theme/icons';
import BaseAsset from './BaseAsset';
import i18n from 'i18n/i18n';

const localize = i18n.loc.localize;

const addProviders = [{
	type: 'dropbox',
	name: 'Dropbox',
	icon: <DropboxIcon width='33px' height='30px'/>
}, {
	type: 'google',
	name: 'Google Drive',
	icon: <GoogleDriveIcon width='36px' height='30px'/>
}, {
	type: 'microsoft',
	name: 'One Drive',
	icon: <OneDriveIcon width='42px' height='25px'/>
}];

const DROPBOX_TIMEOUT = 500;

const styles = theme => ({
	field: {
		margin: 0
	},
	iframeClass: {
		display: 'none'
	},
	deleteButton: {
		padding: 0,
		minWidth: 20
	},
	folderItem: {
		borderBottom: '0.5px solid rgba(0, 0, 0, 0.25)',
		height: 60
	},
	icon: {
		margin: 0,
		cursor: 'pointer',
		color: theme.palette.colors.grey,
		'&[disabled]': {
			fill: theme.palette.action.disabled,
			pointerEvents: 'none'
		},
	},
	label: {
		fontSize: 14,
		lineHeight: '21px',
		color: 'rgba(40, 40, 40, 0.75)',
		marginRight: 2
	},
	folder: {
		marginTop: 1.5 * theme.spacing.unit,
		borderTop: '0.5px solid rgba(0, 0, 0, 0.25)',
	},
	bold: {
		fontWeight: 'bold'
	},
	teamFolderInfo: {
		marginTop: 4 * theme.spacing.unit
	},
	providerRoot: {
		marginRight: 30
	},
	providerButton: {
		marginTop: theme.spacing.unit,
		width: 70,
		height: 70,
		minWidth: 70,
		borderRadius: 20,
		marginBottom: theme.spacing.unit
	},
	providerLabel: {
		marginTop: -20,
		textAlign: 'center',
		fontSize: 10,
		color: 'rgba(40, 40, 40, 0.75)'
	},
	root: {
		width: '100%'
	},
	sectionHeader: {
		display: 'flex',
		alignItems: 'center',
		width: 200
	},
	sectionContainer: {
		marginTop: theme.spacing.unit,
		display: 'flex'
	},
	folderIcon: {
		marginRight: theme.spacing.unit,
		color: theme.palette.colors.grey
	},
	errorClass: {
		marginTop: 4 * theme.spacing.unit,
		fontSize: 12,
		color: '#BC1D1D !important',
		fontStyle: 'italic'
	},
	addFolderButtonContainer: {
		display: 'flex',
		marginTop: 3 * theme.spacing.unit,
		justifyContent: 'flex-end',
		marginRight: 4 * theme.spacing.unit,
	},
	table: {
		borderTop: `1px solid ${theme.palette.colors.colorWithAlpha(theme.palette.colors.zeta, 0.15)}`
	},
	tableCell: {
		'&:first-child': {
			width: '1%' // fit to content
		},
		'&:last-child': {
			width: 50
		},
		height: 60
	},
	fullHeight: {
		height: '100%'
	}
});

@withStyles(styles)
class Asset extends BaseAsset {

	constructor() {
		super();
		this.handleFolderRemove = this.handleFolderRemove.bind(this);
	}

	getProvider() {
		let provider = window.location.hash.match(/.providerid=(.+)&host/);

		return provider && decodeURIComponent(provider[1]);
	}

	componentWillReceiveProps(nextProps, nextContext) {
		if (this.props.type !== nextProps.type) {
			this.setState({ showProviders: false });
		}
	}

	componentDidMount() {
		const providerId = this.getProvider();
		const { changeFolder, match: { params } } = this.props;

		if (providerId && params.tab && params.tab.toLowerCase() === 'assets') {
			changeFolder('root', providerId, {
				fileId: null,
				providerId
			});
			this.setState({ selectedFolder: 'root' + providerId });
		}
	}

	state = {
		showProviders: false
	};

	toggleFolder(path) {
		const state = !this.state[path];

		this.setState({
			[path]: state
		});
	}

	hasCloudFolders() {
		const { company: { folders }, type } = this.props;
		const folderDirectories = folders[type];

		return folderDirectories && folderDirectories.length > 0;
	}

	displayEmptyFolder(options) {
		return (<div>
			<Button
				onClick={() => this.toggleFolder(options.path)}>
				<Typography variant='body1'>{localize('asset-browse-drives')}</Typography>
			</Button>
		</div>);
	}

	displayFolder(options) {
		const { classes, cloudFolders } = this.props;
		const folder = cloudFolders[options.path];
		return (<div className={classes.folder}>
			<Typography variant='body1'>{folder.name}</Typography>
		</div>);
	}

	renderFolderSection(options) {
		const { classes, cloudFolders } = this.props;
		const folder = cloudFolders[options.path];

		return (<div>
			<div className={classes.sectionContainer}>
				<div className={classes.sectionHeader}>
					<FolderIcon className={classes.folderIcon}/><Typography variant='body1'>{options.name}</Typography>
				</div>
				{!folder && this.displayEmptyFolder(options)}
				{folder && this.displayFolder(options)}
			</div>
			{this.state[options.path] && <CloudBrowser/>}
		</div>);
	}

	handleProviderClick(type, companyId) {
		if (type === 'dropbox') {
			this.setState({ dropboxLogout: true, provider: 'dropbox', companyId });
			return;
		}

		authenticateProvider(type, companyId);
	}

	renderCloudProviders() {
		const hasCloudFolders = this.hasCloudFolders();

		if (hasCloudFolders && !this.state.showProviders) {
			return;
		}

		const { companyId, classes } = this.props;

		return (<Grid container>
			{addProviders.map(provider => {
				return (<div key={provider.type} className={classes.providerRoot}>
					<Button
						data-tid={`button-asset-provider-${provider.type}`}
						className={classes.providerButton}
						key={provider.type}
						onClick={() => this.handleProviderClick(provider.type, companyId)}
					>
						{provider.icon}
					</Button>
					<Typography className={classes.providerLabel}>{provider.name}</Typography>
				</div>);
			})}
		</Grid>);
	}

	removeFolder({ fileId, providerId }) {
		const { companyId, removeFolder, fetchSession, type } = this.props;

		removeFolder({ fileId, providerId, type, companyId })
			.then(fetchSession);
	}

	renderTeamFolder() {
		if (!this.hasCloudFolders() || this.state.showProviders) {
			return null;
		}

		const { classes, company: { folders }, type } = this.props;
		const folderDirectories = folders[type];

		return (<div className={classes.teamFolderInfo}>
			<Typography style={{ paddingBottom: 10 }} className={classNames(classes.label, classes.bold)}>
				{localize('asset-folders', type)}
			</Typography>

			<Table className={classes.table}>
				<TableBody>
					{
						folderDirectories.map(document => {
							return (
								<TableRow key={document.fileId} hover>
									<TableCell padding='none' className={classes.tableCell}>
										<FolderIcon className={classes.folderIcon}/>
									</TableCell>
									<TableCell padding='none' className={classes.tableCell}>
										<Grid
											container
											justify="space-between"
											alignItems="center"
											alignContent="center">
											{this.renderFolderInfo(document)}
											{this.renderEditFolderField(document)}
											<Grid item>
												<Grid container className={classes.fullHeight} alignContent="center">
													{this.renderRenameButton(document)}
													{this.renderSaveButton(document)}
												</Grid>
											</Grid>
										</Grid>
									</TableCell>
									<TableCell align='right' padding='none' className={classes.tableCell}>
										<IconButton
											color='secondary'
											component='span'
											aria-label='Delete'
											onClick={() => this.handleFolderRemove(document)}
										>
											<DeleteIcon/>
										</IconButton>
									</TableCell>
								</TableRow>
							);
						})
					}
				</TableBody>
			</Table>

		</div>);
	}

	handleFolderRemove(document) {
		this.props.showModal('CONFIRMATION_MODAL', {
			message: localize('asset-folder-remove-message', document.displayName),
			buttons: [
				<Button
					data-tid="button-remove-asset-cancel"
					key='cancel-folder-button'
					variant='outlined'
					color='primary'
				>
					{localize('cancel')}
				</Button>,
				<Button
					key='delete-folder-button'
					data-tid="button-remove-asset-confirm"
					variant='contained'
					color='primary'
					onClick={() => this.removeFolder(document)}
				>
					{localize('asset-folder-remove-button-remove')}
				</Button>
			]
		});
	}

	handleOnLoad() {
		const { companyId, provider } = this.state;

		// HACK: Without this timeout it might happen(very rare) that dropbox doesn't log out user before we hit login page.
		setTimeout(() => authenticateProvider(provider, companyId), DROPBOX_TIMEOUT);
	}

	dropboxLogout() {
		const { dropboxLogout } = this.state;
		const { classes: { iframeClass } } = this.props;

		if (!dropboxLogout) {
			return;
		}

		return (<iframe src="https://www.dropbox.com/logout" className={iframeClass} onLoad={this.handleOnLoad()}/>);
	}

	renderAddTeamFolderButton() {
		if (!this.hasCloudFolders() || this.state.showProviders) {
			return;
		}

		const { classes: { addFolderButtonContainer } } = this.props;

		return (<div className={addFolderButtonContainer}><Button
			variant='contained'
			color='primary'
			data-tid='button-asset-add'
			onClick={() => this.setState({ showProviders: true })}>{localize('asset-folder-add-button')}</Button></div>);
	}

	renderBasicView() {
		return (<div>
			{this.renderCloudProviders()}
			{this.renderTeamFolder()}
			{this.renderAddTeamFolderButton()}
			{this.dropboxLogout()}
		</div>);
	}

	render() {
		const { classes, type, match } = this.props;
		const { selectedFolder } = this.state;
		const displayCloudBrowser = Boolean(this.getProvider());

		return (<div className={classes.root}>
			{!displayCloudBrowser && this.renderBasicView()}
			{displayCloudBrowser && <CloudBrowser selectedFolder={selectedFolder} type={type} match={match}/>}
		</div>);
	}

}

const mapStateToProps = state => ({
	cloudFolders: {
		...state.cloudFolders,
		documentFolder: null,
		imageFolder: null
	},
	company: state.companies.rawDataById[state.companies.selectedId] || {},
	companyId: state.companies.selectedId,
});

const mapDispatchToProps = dispatch => bindActionCreators({
	fetchSession,
	updateFolder,
	changeFolder,
	removeFolder,
	showModal
}, dispatch);

Asset.propTypes = {
	match: PropTypes.object.isRequired,
	updateFolder: PropTypes.func,
	fetchSession: PropTypes.func,
	removeFolder: PropTypes.func,
	changeFolder: PropTypes.func,
	showModal: PropTypes.func,
	classes: PropTypes.object,
	cloudFolders: PropTypes.object,
	company: PropTypes.object,
	companyId: PropTypes.string,
	type: PropTypes.string.isRequired
};

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(Asset);



// WEBPACK FOOTER //
// ./src/components/TeamFolders/Asset.js